"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { X, Menu } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import DownloadCTA from "@/components/shared/DownloadCTA";
import MegaMenu from "@/components/nav/MegaMenu";
import MobileMegaMenu from "@/components/nav/MobileMegaMenu";
import { TRACKER_ARTICLE_PATH, type RatgeberColumn } from "@/lib/ratgeber";

// Aufrück-Tracker steht bewusst als eigener, erster Menüpunkt vor „Ratgeber" –
// separat gerendert (siehe unten), nicht Teil dieser Liste.
const trackerLink = {
  label: "Aufrück-Tracker",
  href: TRACKER_ARTICLE_PATH,
  badge: "Neu",
};

const navLinks = [
  { label: "Universitäten", href: "/unis-in-deutschland/" },
  { label: "Lexikon", href: "/lexikon/" },
  { label: "Über uns", href: "/ueber-uns/" },
  { label: "FAQ", href: "/faq/" },
  { label: "Kontakt", href: "/kontakt/" },
];

/** Partner-Logo für Kooperations-Landingpages (neben dem ncrechner-Logo). */
export interface NavbarPartner {
  name: string;
  logoSrc: string;
  logoWidth: number;
  logoHeight: number;
  href: string;
}

/**
 * Client-Shell der Navigation. `ratgeberColumns` kommt als Prop von der
 * Server-Komponente `Navbar` (components/Navbar.tsx) – siehe dort, warum das
 * nicht einfach hier importiert wird.
 */
export default function NavbarClient({
  ratgeberColumns,
  partner,
}: {
  ratgeberColumns: RatgeberColumn[];
  /** Co-Branding: zeigt „ncrechner × Partner“ und blendet „App laden“ aus. */
  partner?: NavbarPartner;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  // Hintergrund-Scroll sperren, solange das mobile Menü offen ist
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    // sticky mit negativem top: die Ankündigungsleiste (h-10) scrollt hinaus,
    // die eigentliche Navigation bleibt oben haften.
    <header className="sticky top-[-2.5rem] z-50 bg-paper/90 backdrop-blur-md border-b border-line">
      {/* Mini-Leiste: Aufrück-Tracker-Ankündigung */}
      <Link
        href="/ratgeber/zulassungsrechner/aufruecken-im-vergabeverfahren/"
        className="block bg-accent text-paper hover:bg-accent-deep transition-colors"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-center gap-3 text-sm">
          <span className="truncate">
            <span className="font-semibold">Jetzt neu:</span> Aufrück-Tracker für
            das Vergabeverfahren von hochschulstart.de
          </span>
          <span className="shrink-0 rounded-full bg-paper text-accent px-3 py-1 text-xs font-semibold">
            Mehr erfahren
          </span>
        </div>
      </Link>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo (+ Partner-Logo auf Kooperations-LPs) */}
        <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
          <Link href="/" className="flex items-center shrink-0" aria-label="ncrechner Startseite">
            <Image
              src="/images/logo-wordmark.webp"
              alt="ncrechner"
              width={386}
              height={88}
              className={partner ? "h-6 sm:h-7 w-auto" : "h-7 w-auto"}
              priority
            />
          </Link>
          {partner && (
            <>
              <span className="text-ink-faint text-sm font-medium" aria-hidden="true">
                ×
              </span>
              <a
                href={partner.href}
                target="_blank"
                rel="noopener"
                className="flex items-center shrink-0"
                aria-label={`In Kooperation mit ${partner.name}`}
              >
                <Image
                  src={partner.logoSrc}
                  alt={partner.name}
                  width={partner.logoWidth}
                  height={partner.logoHeight}
                  className="h-4 min-[380px]:h-5 xl:h-6 w-auto"
                  priority
                />
              </a>
            </>
          )}
        </div>

        {/* Desktop nav – erst ab lg (mit Partner-Logo ab xl), darunter reicht der Platz nicht für alle Einträge */}
        <div className={cn("hidden items-center gap-5", partner ? "xl:flex" : "lg:flex")}>
          {/* Aufrück-Tracker: eigener, dominanter erster Menüpunkt vor „Ratgeber" */}
          <Link
            href={trackerLink.href}
            className={cn(
              "relative inline-flex items-center gap-2 text-sm transition-colors duration-200",
              isActive(trackerLink.href)
                ? "text-ink font-semibold"
                : "text-ink-soft font-medium hover:text-ink"
            )}
          >
            <span
              className={cn(
                "w-1.5 h-1.5 bg-accent transition-opacity duration-200",
                isActive(trackerLink.href) ? "opacity-100" : "opacity-0"
              )}
              aria-hidden="true"
            />
            {trackerLink.label}
            <span className="rounded-full bg-mint-soft text-mint-deep px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">
              {trackerLink.badge}
            </span>
          </Link>
          <MegaMenu ratgeberColumns={ratgeberColumns} />
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "relative inline-flex items-center gap-2 text-sm transition-colors duration-200",
                  active
                    ? "text-ink font-semibold"
                    : "text-ink-soft font-medium hover:text-ink"
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 bg-accent transition-opacity duration-200",
                    active ? "opacity-100" : "opacity-0"
                  )}
                  aria-hidden="true"
                />
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* CTA (auf Kooperations-LPs ausgeblendet: dort zählt der Seiten-CTA) */}
        <div className={cn("hidden items-center", !partner && "lg:flex")}>
          <DownloadCTA
            variant="accent"
            label="App laden"
            className="px-5 py-2.5 text-sm min-h-[40px]"
          />
        </div>

        {/* Mobile: App-laden bleibt immer sichtbar, auch bei geschlossenem Menü */}
        <div className={cn("flex items-center gap-2", partner ? "xl:hidden" : "lg:hidden")}>
          {!partner && (
            <DownloadCTA
              variant="accent"
              label="App laden"
              className="px-4 py-2 text-sm min-h-[36px]"
            />
          )}
          <button
            className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg text-ink hover:bg-paper-deep transition-colors cursor-pointer"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={cn("overflow-hidden bg-paper border-b border-line", partner ? "xl:hidden" : "lg:hidden")}
          >
            <div className="px-4 py-4 flex flex-col max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain [padding-bottom:calc(1.5rem+env(safe-area-inset-bottom))]">
              {/* Aufrück-Tracker: eigener, dominanter erster Menüpunkt vor „Ratgeber" */}
              <Link
                href={trackerLink.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded-lg font-medium transition-colors",
                  isActive(trackerLink.href)
                    ? "text-ink font-semibold bg-paper-deep"
                    : "text-ink-soft hover:bg-paper-deep hover:text-ink"
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 bg-accent shrink-0",
                    isActive(trackerLink.href) ? "opacity-100" : "opacity-0"
                  )}
                  aria-hidden="true"
                />
                {trackerLink.label}
                <span className="rounded-full bg-mint-soft text-mint-deep px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">
                  {trackerLink.badge}
                </span>
              </Link>
              <MobileMegaMenu
                ratgeberColumns={ratgeberColumns}
                onNavigate={() => setMobileOpen(false)}
              />
              {navLinks.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 rounded-lg font-medium transition-colors",
                      active
                        ? "text-ink font-semibold bg-paper-deep"
                        : "text-ink-soft hover:bg-paper-deep hover:text-ink"
                    )}
                  >
                    <span
                      className={cn(
                        "w-1.5 h-1.5 bg-accent shrink-0",
                        active ? "opacity-100" : "opacity-0"
                      )}
                      aria-hidden="true"
                    />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
